"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import Image from "next/image";
import { motion, useScroll, useTransform, type MotionValue } from "framer-motion";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import { HomeCtaSection } from "@/features/home/presentation/components/home-cta-section";
import type { HomePageContent } from "@/features/home/types/home-page-content";
import { STEPS, MARQUEE_ROW_1, MARQUEE_ROW_2 } from "../../constants/steps";
import type { HowItWorksStep } from "../../types/step.types";

/* ------------------------------------------------------------------ */
/*  Sticky Card Component                                             */
/* ------------------------------------------------------------------ */

function StickyStepCard(t0) {
  const $ = _c(40);
  const {
    step,
    index,
    total,
    parentScrollProgress
  } = t0;
  const isLast = index === total - 1;
  const stepSize = 1 / total;
  const start = index * stepSize;
  const end = (index + 1) * stepSize;
  let t1;
  if ($[0] !== end || $[1] !== start) {
    t1 = [start, end];
    $[0] = end;
    $[1] = start;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [1, 0.97];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const scale = useTransform(parentScrollProgress, t1, t2);
  let t3;
  if ($[4] !== end || $[5] !== start) {
    t3 = [start, end];
    $[4] = end;
    $[5] = start;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [1, 0.9];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const opacity = useTransform(parentScrollProgress, t3, t4);
  const t5 = `calc(100px + ${index * 60}px)`;
  const t6 = index + 10;
  const t7 = isLast ? 1 : scale;
  const t8 = isLast ? 1 : opacity;
  let t9;
  if ($[8] !== t5 || $[9] !== t6 || $[10] !== t7 || $[11] !== t8) {
    t9 = {
      top: t5,
      zIndex: t6,
      scale: t7,
      opacity: t8
    };
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = step.id;
  let t11;
  if ($[13] !== step.title) {
    t11 = step.title.toUpperCase();
    $[13] = step.title;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== step.id || $[16] !== t11) {
    t12 = <div className="flex h-[60px] shrink-0 items-center border-b border-[#EAE5DA] bg-[#FAF8F5] px-8 sm:px-12 lg:px-16"><span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#C06A42] uppercase sm:text-xs">STEP {t10} · {t11}</span></div>;
    $[15] = step.id;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== step.title) {
    t13 = <h3 className="mb-6 font-heading text-[2rem] leading-tight font-normal tracking-tight text-slate-900 sm:text-[2.5rem]">{step.title}</h3>;
    $[18] = step.title;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== step.description) {
    t14 = <p className="mb-8 text-[1.05rem] leading-relaxed font-light text-slate-600 sm:text-[1.125rem]">{step.description}</p>;
    $[20] = step.description;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== step.tagline) {
    t15 = <div className="inline-block rounded-full border border-[#EAE5DA] bg-[#EAE5DA]/30 px-6 py-2.5 text-xs font-semibold tracking-wider text-slate-700 uppercase">{step.tagline}</div>;
    $[22] = step.tagline;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t13 || $[25] !== t14 || $[26] !== t15) {
    t16 = <div className="flex-1 lg:max-w-xl">{t13}{t14}{t15}</div>;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== step.imageUrl || $[29] !== step.title) {
    t17 = <div className="w-full flex-1 lg:max-w-[480px]"><div className="relative aspect-[4/3] w-full overflow-hidden rounded-[1.5rem] border border-[#EAE5DA] bg-slate-50 shadow-[0_8px_30px_rgba(0,0,0,0.03)]"><Image src={step.imageUrl} alt={step.title} fill={true} sizes="(max-width: 768px) 100vw, 480px" className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" /></div></div>;
    $[28] = step.imageUrl;
    $[29] = step.title;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = <div className="flex flex-col items-center gap-10 p-8 sm:p-12 lg:flex-row lg:gap-20 lg:p-20">{t16}{t17}</div>;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t12 || $[35] !== t18) {
    t19 = <div className="group mx-auto flex max-w-[1320px] flex-col overflow-hidden rounded-[2.5rem] border border-[#EAE5DA] bg-[#FAF8F5] shadow-[0_12px_40px_rgba(0,0,0,0.04)] transition-shadow duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)]">{t12}{t18}</div>;
    $[34] = t12;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t19 || $[38] !== t9) {
    t20 = <motion.div className="sticky w-full origin-top" style={t9}>{t19}</motion.div>;
    $[37] = t19;
    $[38] = t9;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  return t20;
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

interface HowItWorksPageProps {
  content: HomePageContent;
}
export function HowItWorksPage(t0) {
  const $ = _c(38);
  const {
    content
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      target: containerRef,
      offset: ["start start", "end end"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t1);
  let t2;
  if ($[1] !== content.navigation) {
    t2 = <SiteNavbar content={content.navigation} />;
    $[1] = content.navigation;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute inset-0 z-0"><div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(125,226,248,0.15),transparent_50%)]" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 20
    };
    t5 = {
      opacity: 1,
      y: 0
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <motion.p className="mb-6 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-medium tracking-[0.32em] text-white/80 uppercase backdrop-blur-sm" initial={t4} animate={t5} transition={{
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    }}>How it works</motion.p>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 30
    };
    t8 = {
      opacity: 1,
      y: 0
    };
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.8,
      delay: 0.1,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <motion.h1 className="max-w-7xl font-heading text-5xl font-normal tracking-tight text-balance text-white md:text-6xl lg:text-[80px]" initial={t7} animate={t8} transition={t9}>Your healing journey,<br className="hidden sm:block" /> demystified.</motion.h1>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0,
      y: 30
    };
    t12 = {
      opacity: 1,
      y: 0
    };
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <section className="relative flex min-h-[75vh] flex-col items-center justify-center overflow-hidden bg-[#155A7D] px-5 pt-20 sm:px-8 lg:px-12">{t3}<div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center px-6 py-24 text-center sm:px-10 lg:px-12">{t6}{t10}<motion.p className="mx-auto mt-6 mb-10 max-w-2xl text-base font-light text-pretty text-white/88 md:text-lg lg:text-xl" initial={t11} animate={t12} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.25, 0.1, 0.25, 1]
        }}>A simple, secure, and supportive path to finding the right therapist and taking control of your emotional wellness.</motion.p></div></section>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== scrollYProgress) {
    t14 = STEPS.map((step, index) => <StickyStepCard key={step.id} step={step} index={index} total={STEPS.length} parentScrollProgress={scrollYProgress} />);
    $[14] = scrollYProgress;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t14) {
    t15 = <section ref={containerRef} className="relative bg-[#FCFBF7]"><div className="mx-auto max-w-[1440px] px-4 py-24 pb-48 sm:px-6 lg:px-8 lg:py-32 lg:pb-64"><div className="relative">{t14}</div></div></section>;
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  let t17;
  let t18;
  let t19;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      opacity: 0,
      y: 30
    };
    t17 = {
      opacity: 1,
      y: 0
    };
    t18 = {
      once: true
    };
    t19 = {
      duration: 0.7
    };
    $[18] = t16;
    $[19] = t17;
    $[20] = t18;
    $[21] = t19;
  } else {
    t16 = $[18];
    t17 = $[19];
    t18 = $[20];
    t19 = $[21];
  }
  let t20;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="mx-auto mb-16 max-w-4xl px-5 text-center"><motion.h2 initial={t16} whileInView={t17} viewport={t18} transition={t19} className="font-heading text-[2rem] leading-tight font-medium tracking-tight text-slate-900 sm:text-[3rem]">Therapeutic support for <br className="hidden sm:block" /><span className="text-[#3188BE]">whatever you are going through.</span></motion.h2></div>;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  let t22;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      x: ["-50%", "0%"]
    };
    t22 = {
      repeat: Infinity,
      ease: "linear",
      duration: 40
    };
    $[23] = t21;
    $[24] = t22;
  } else {
    t21 = $[23];
    t22 = $[24];
  }
  let t23;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="relative flex w-full overflow-hidden"><motion.div animate={t21} transition={t22} className="flex w-fit gap-4 pr-4 sm:gap-6 sm:pr-6">{[...MARQUEE_ROW_1, ...MARQUEE_ROW_1].map(_temp)}</motion.div></div>;
    $[25] = t23;
  } else {
    t23 = $[25];
  }
  let t24;
  let t25;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      x: ["0%", "-50%"]
    };
    t25 = {
      repeat: Infinity,
      ease: "linear",
      duration: 40
    };
    $[26] = t24;
    $[27] = t25;
  } else {
    t24 = $[26];
    t25 = $[27];
  }
  let t26;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <section className="overflow-hidden bg-[#FCFBF7] pb-32">{t20}<div className="mb-20 flex flex-col gap-6">{t23}<div className="relative flex w-full overflow-hidden"><motion.div animate={t24} transition={t25} className="flex w-fit gap-4 pr-4 sm:gap-6 sm:pr-6">{[...MARQUEE_ROW_2, ...MARQUEE_ROW_2].map(_temp2)}</motion.div></div></div></section>;
    $[28] = t26;
  } else {
    t26 = $[28];
  }
  let t27;
  if ($[29] !== content.ctaSection) {
    t27 = <div className="bg-white"><HomeCtaSection content={content.ctaSection} /></div>;
    $[29] = content.ctaSection;
    $[30] = t27;
  } else {
    t27 = $[30];
  }
  let t28;
  if ($[31] !== content.footer) {
    t28 = <div className="bg-[#0b1416]"><SiteFooter content={content.footer} /></div>;
    $[31] = content.footer;
    $[32] = t28;
  } else {
    t28 = $[32];
  }
  let t29;
  if ($[33] !== t15 || $[34] !== t2 || $[35] !== t27 || $[36] !== t28) {
    t29 = <div className="relative min-h-screen bg-[#FCFBF7] font-sans selection:bg-[#3188BE]/20 selection:text-[#1D4263]">{t2}{t13}{t15}{t26}{t27}{t28}</div>;
    $[33] = t15;
    $[34] = t2;
    $[35] = t27;
    $[36] = t28;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  return t29;
}
function _temp2(item_0, index_1) {
  return <div key={`row2-${index_1}`} className="flex-none rounded-full border border-slate-200/60 bg-white px-8 py-4 text-[1.125rem] font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-300">{item_0}</div>;
}
function _temp(item, index_0) {
  return <div key={`row1-${index_0}`} className="flex-none rounded-full border border-slate-200/60 bg-white px-8 py-4 text-[1.125rem] font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-300">{item}</div>;
}